<script setup lang="ts">
  import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
  import { Editor, EditorContent } from '@tiptap/vue-2';
  import type { ChainedCommands } from '@tiptap/vue-2';
  import StarterKit from '@tiptap/starter-kit';
  import Link from '@tiptap/extension-link';
  import { Markdown } from '@tiptap/markdown';

  const props = withDefaults(
    defineProps<{
      value?: string | null;
      errorMessage?: string | null;
      maxLength?: number | null;
      characterCount?: number | null;
    }>(),
    {
      value: '',
      errorMessage: null,
      maxLength: null,
      characterCount: null,
    }
  );

  const emit = defineEmits<{
    (e: 'input', value: string): void;
    (e: 'change', value: string): void;
  }>();

  const editor = ref<Editor | null>(null);
  const linkDraft = ref('');
  const linkEditorVisible = ref(false);

  const normalizedValue = computed(() =>
    (props.value ?? '').replace(/\r\n?/g, '\n')
  );

  const toolbarState = computed(() => ({
    bold: editor.value?.isActive('bold') ?? false,
    italic: editor.value?.isActive('italic') ?? false,
    bulletList: editor.value?.isActive('bulletList') ?? false,
    orderedList: editor.value?.isActive('orderedList') ?? false,
    link: editor.value?.isActive('link') ?? false,
  }));

  const counterLabel = computed(() => {
    if (props.characterCount === null && props.maxLength === null) {
      return null;
    }

    if (props.maxLength === null) {
      return `${props.characterCount ?? 0}`;
    }

    return `${props.characterCount ?? 0} / ${props.maxLength}`;
  });

  const isOverLimit = computed(() => {
    if (props.maxLength === null || props.characterCount === null) return false;

    return props.characterCount > props.maxLength;
  });

  function emitMarkdown() {
    if (!editor.value) return;

    const markdown = editor.value.getMarkdown();
    emit('input', markdown);
    emit('change', markdown);
  }

  // Blank markdown parses into a document without any paragraph, which leaves the
  // cursor nowhere to go, so blank values are loaded as plain empty content instead.
  function getContentType(markdown: string) {
    return markdown.trim() ? 'markdown' : undefined;
  }

  // Focus before creating the chain: focusing dispatches its own transaction (right
  // away on touch devices), which would leave an already created chain out of date.
  function runCommand(command: (chain: ChainedCommands) => ChainedCommands) {
    const instance = editor.value;
    if (!instance) return;

    if (!instance.view.hasFocus()) instance.view.focus();
    command(instance.chain().scrollIntoView()).run();
  }

  function toggleBold() {
    runCommand((chain) => chain.toggleBold());
  }

  function toggleItalic() {
    runCommand((chain) => chain.toggleItalic());
  }

  function toggleBulletList() {
    runCommand((chain) => chain.toggleBulletList());
  }

  function toggleOrderedList() {
    runCommand((chain) => chain.toggleOrderedList());
  }

  function insertHardBreak() {
    runCommand((chain) => chain.setHardBreak());
  }

  function openLinkEditor() {
    if (!editor.value) return;

    linkDraft.value = editor.value.getAttributes('link').href ?? '';
    linkEditorVisible.value = true;
  }

  function cancelLinkEditor() {
    linkDraft.value = '';
    linkEditorVisible.value = false;
  }

  function applyLink() {
    const href = linkDraft.value.trim();

    runCommand((chain) => {
      const linkChain = chain.extendMarkRange('link');
      if (!href) return linkChain.unsetLink();

      return linkChain.setLink({
        href,
        target: '_blank',
        rel: 'noopener noreferrer',
      });
    });

    cancelLinkEditor();
  }

  function removeLink() {
    runCommand((chain) => chain.extendMarkRange('link').unsetLink());
    cancelLinkEditor();
  }

  onMounted(() => {
    editor.value = new Editor({
      content: normalizedValue.value,
      contentType: getContentType(normalizedValue.value),
      extensions: [
        StarterKit.configure({
          blockquote: false,
          code: false,
          codeBlock: false,
          heading: false,
          horizontalRule: false,
          strike: false,
        }),
        Link.configure({
          autolink: true,
          linkOnPaste: true,
          openOnClick: false,
        }),
        Markdown,
      ],
      editorProps: {
        attributes: {
          class: 'branch-markdown-editor__surface-content',
        },
      },
      onUpdate: emitMarkdown,
    });
  });

  watch(
    () => props.value,
    (value) => {
      if (!editor.value) return;

      const nextValue = (value ?? '').replace(/\r\n?/g, '\n');
      if (editor.value.getMarkdown() === nextValue) return;

      editor.value.commands.setContent(nextValue, {
        contentType: getContentType(nextValue),
      });
    }
  );

  onUnmounted(() => {
    const instance = editor.value;
    editor.value = null;
    instance?.destroy();
  });
</script>

<template>
  <div class="branch-markdown-editor">
    <div class="branch-markdown-editor__toolbar">
      <v-btn
        class="branch-markdown-editor__action"
        :class="{ 'branch-markdown-editor__action--active': toolbarState.bold }"
        type="button"
        small
        text
        @click="toggleBold"
      >
        Negrita
      </v-btn>
      <v-btn
        class="branch-markdown-editor__action"
        :class="{
          'branch-markdown-editor__action--active': toolbarState.italic,
        }"
        type="button"
        small
        text
        @click="toggleItalic"
      >
        Cursiva
      </v-btn>
      <v-btn
        class="branch-markdown-editor__action"
        :class="{
          'branch-markdown-editor__action--active': toolbarState.bulletList,
        }"
        type="button"
        small
        text
        @click="toggleBulletList"
      >
        Lista
      </v-btn>
      <v-btn
        class="branch-markdown-editor__action"
        :class="{
          'branch-markdown-editor__action--active': toolbarState.orderedList,
        }"
        type="button"
        small
        text
        @click="toggleOrderedList"
      >
        Numerada
      </v-btn>
      <v-btn
        class="branch-markdown-editor__action"
        :class="{ 'branch-markdown-editor__action--active': toolbarState.link }"
        type="button"
        small
        text
        @click="openLinkEditor"
      >
        Enlace
      </v-btn>
      <v-btn
        class="branch-markdown-editor__action"
        type="button"
        small
        text
        @click="insertHardBreak"
      >
        Salto
      </v-btn>
    </div>

    <div v-if="linkEditorVisible" class="branch-markdown-editor__link-editor">
      <v-text-field
        v-model="linkDraft"
        class="branch-markdown-editor__link-input"
        label="URL del enlace"
        placeholder="https://..."
        hide-details
        outlined
        dense
      />
      <div class="branch-markdown-editor__link-actions">
        <v-btn
          class="branch-markdown-editor__link-button"
          type="button"
          small
          color="primary"
          depressed
          @click="applyLink"
        >
          Aplicar
        </v-btn>
        <v-btn
          class="branch-markdown-editor__link-button"
          type="button"
          small
          text
          @click="removeLink"
        >
          Quitar
        </v-btn>
        <v-btn
          class="branch-markdown-editor__link-button"
          type="button"
          small
          text
          @click="cancelLinkEditor"
        >
          Cancelar
        </v-btn>
      </div>
    </div>

    <div class="branch-markdown-editor__surface">
      <EditorContent :editor="editor" />
    </div>

    <div v-if="counterLabel" class="branch-markdown-editor__meta">
      <span
        class="branch-markdown-editor__counter"
        :class="{
          'branch-markdown-editor__counter--over-limit': isOverLimit,
        }"
      >
        {{ counterLabel }}
      </span>
    </div>

    <p v-if="errorMessage" class="branch-markdown-editor__error">
      {{ errorMessage }}
    </p>
  </div>
</template>

<style scoped lang="scss">
  .branch-markdown-editor {
    @apply tw-space-y-3;

    &__toolbar {
      @apply tw-flex tw-flex-wrap tw-gap-2;
    }

    &__action {
      @apply tw-normal-case;

      &--active {
        @apply tw-bg-blue-50 tw-text-blue-700;
      }
    }

    &__link-editor {
      @apply tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-neutral-50 tw-p-3 tw-space-y-3;
    }

    &__link-actions {
      @apply tw-flex tw-flex-wrap tw-gap-2;
    }

    &__surface {
      @apply tw-rounded-2xl tw-border tw-border-neutral-200 tw-bg-white tw-p-4 tw-shadow-sm;
    }

    &__meta {
      @apply tw-flex tw-justify-end;
    }

    &__counter {
      @apply tw-text-xs tw-font-medium tw-text-neutral-500;

      &--over-limit {
        @apply tw-text-red-600;
      }
    }

    &__error {
      @apply tw-text-sm tw-font-medium tw-text-red-600;
    }
  }

  :deep(.branch-markdown-editor__surface-content) {
    @apply tw-outline-none tw-text-neutral-700 tw-leading-6;
    min-height: 180px;

    p,
    ul,
    ol {
      @apply tw-my-2;
    }

    ul,
    ol {
      @apply tw-pl-5;
    }

    ul {
      @apply tw-list-disc;
    }

    ol {
      @apply tw-list-decimal;
    }

    ul > li {
      @apply tw-list-disc;
    }

    ol > li {
      @apply tw-list-decimal;
    }

    a {
      @apply tw-text-blue-600 tw-underline;
    }
  }
</style>
